/**
 * Module dependencies.
 */

import {
  Field,
  FormField,
  InputTheme,
  Label,
  Message
} from 'src/components/core/forms/styles';

import { ForwardedRef, forwardRef } from 'react';
import { Loading } from 'src/components/core/loading';
import { Svg } from 'src/components/core/svg';
import { assignRefs } from 'src/core/utils/refs';
import { ifProp } from 'styled-tools';
import { useTranslation } from 'next-i18next';
import chevronDownSvg from 'src/assets/svg/chevron-down.svg';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

/**
 * `SelectOption` type.
 */

type SelectOption = {
  label: string;
  value: any;
};

/**
 * Export `SelectProps` type.
 */

export type SelectProps = React.HTMLAttributes<HTMLSelectElement> & {
  disabled?: boolean;
  error?: string;
  hideEmptyOption?: boolean;
  info?: string;
  isLoading?: boolean;
  isRequired?: boolean;
  label?: string;
  options: SelectOption[];
  readOnly?: boolean;
  theme?: InputTheme;
  value: SelectOption['value'] | undefined;
};

/**
 * `StyledField` styled component.
 */

const StyledField = styled(Field)`
  opacity: 1;
  position: relative;
`;

/**
 * `Chevron` styled component.
 */

const Chevron = styled(Svg).attrs({
  icon: chevronDownSvg,
  size: '48px'
})`
  color: var(--color-sage700);
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
`;

/**
 * `StyledLoading` styled component.
 */

const StyledLoading = styled(Loading)`
  color: var(--color-sage700);
  left: auto;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
`;

/**
 * `StyledSelect` styled component.
 */

const StyledSelect = styled.select<{ hasValue: boolean }>`
  appearance: none;
  background: transparent;
  border: none;
  color: ${ifProp(
    'hasValue',
    'var(--color-gray700)',
    'var(--input-placeholder-color)'
  )};

  height: 48px;
  width: 100%;

  &:hover,
  &:focus {
    color: var(--input-active-text-color);
    outline: none;
  }
`;

/**
 * `SelectComponent` component.
 */

function SelectComponent(props: SelectProps, forwardedRef: ForwardedRef<any>) {
  const { t } = useTranslation('common');
  const {
    error,
    hideEmptyOption,
    info,
    isLoading,
    isRequired,
    label,
    options,
    theme = 'dark',
    value,
    ...inputProps
  } = props;

  const isValueEmpty = isEmpty(value);

  return (
    <FormField
      disabled={inputProps.disabled || inputProps.readOnly || isLoading}
      hasError={!!error}
      theme={theme}
    >
      {label && (
        <Label
          // @ts-ignore
          htmlFor={inputProps?.name}
          isRequired={!!isRequired}
        >
          {label}
        </Label>
      )}

      <StyledField
        data-has-value={!isValueEmpty}
        hasIcon
      >
        <StyledSelect
          // @ts-ignore
          id={inputProps?.name}
          {...inputProps}
          hasValue={!isValueEmpty}
          ref={assignRefs(forwardedRef)}
        >
          {!hideEmptyOption && (
            <option
              disabled
              selected={isValueEmpty}
              value={''}
            >
              {t('common:actions.select')}
            </option>
          )}

          {options.map(option => (
            <option
              key={option.value}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </StyledSelect>

        {isLoading ? <StyledLoading /> : <Chevron />}
      </StyledField>

      {(info || error) && (
        <Message type={error ? 'error' : 'info'}>{error ?? info}</Message>
      )}
    </FormField>
  );
}

/**
 * Export `Select`.
 */

export const Select = forwardRef<SelectProps, any>(SelectComponent);
